/**
 * StageBreakdown — shared stage / company / activity timeline renderer used
 * by both UseCaseDetailPanel (UC-scoped) and IndustryDetailPanel (industry-
 * level roll-up). Both call GetUcPipelineDetail; the industry-level call omits
 * ucName so each PipelineOpp carries its own ucName which surfaces in the
 * company row here.
 *
 * Everything the component needs — stages, opps, activities — lives on the
 * `detail` prop. The parent owns fetching + loading/error states.
 */

import { useMemo, useState } from 'react';
import { Link, Popover, StatusIndicator, type StatusIndicatorProps } from '@amzn/awsui-components-react';
import {
  OppStatus,
  type GetUcPipelineDetailOutput,
  type StageGroup,
  type PipelineOpp,
  type Activity,
} from '../../../client/IvtHubClient';
import { RELEVANCE_STYLES } from '../../../utils/scoreStyle';
import { oppSfdcUrl } from '../../../utils/sfdc';
import { ActivityIcon } from '../../../components/ActivityIcon/ActivityIcon';

// PipelineType bucket membership. Backend `PipelineOpp.pipelineTypes` carries
// every bucket the opp belongs to (see uc-pipeline.smithy PipelineType enum
// doc), so the frontend filter is a straight `.includes(...)` check — no
// client-side re-derivation of bucket rules. Empty / missing list means the
// opp doesn't participate in any of the 5 buckets (e.g. Closed Lost opps).
function oppMatchesPipelineType(opp: PipelineOpp, filter: string): boolean {
  return (opp.pipelineTypes ?? []).includes(filter as never);
}

// ── Stage config ───────────────────────────────────────────────────────────

const STAGE_ORDER = ['Qualified', 'Technical Validation', 'Business Validation', 'Committed', 'Launched'];
const STAGE_LABELS: Record<string, string> = {
  Qualified: '确认 Qualified',
  'Technical Validation': '技术验证 Tech Validation',
  'Business Validation': '商务验证 Biz Validation',
  Committed: '承诺 Committed',
  Launched: '上线 Launched',
};
const STAGE_TAG_COLORS: Record<string, string> = {
  Qualified: '#22C55E',
  'Technical Validation': '#EF4444',
  'Business Validation': '#3B82F6',
  Committed: '#A855F7',
  Launched: '#EAB308',
  Others: '#94A3B8',
};

// Relevance score styles moved to ../../../utils/scoreStyle (shared with the
// Pipeline List Score column). StageBreakdown keeps its own `score >= 1 && <= 3`
// gate (0 = unscored → no badge here), so it uses RELEVANCE_STYLES directly.

// Backend pre-renders statusLabel with emoji prefixes; StatusIndicator has its
// own icon, so we strip the leading emoji to avoid a doubled icon.
function stripStatusEmoji(label: string): string {
  return label.replace(/^\s*[^\p{L}\p{N}]+\s*/u, '').trim();
}

// Advice bank keyed by relevance score. TODO: swap for backend `advice[]`.
function suggestedActions(score: 1 | 2 | 3, asset?: string): string[] {
  if (score === 3) {
    const acts = ['持续跟进该客户的上线进度，确保 Asset 实际部署', '收集客户使用反馈，沉淀为 Asset 优化输入'];
    if (asset) acts.push(`将此案例纳入 ${asset} 的 wins 清单`);
    return acts;
  }
  if (score === 2) {
    const acts = ['安排一次 Asset Demo 或 Workshop 加速推进'];
    if (asset) acts.push(`准备 ${asset} 的定制化 briefing 材料`);
    acts.push('评估是否可以升级为 POC/PoV');
    return acts;
  }
  return [
    '确认客户需求与 UC Asset 的匹配度',
    '考虑引入 IS/ISA 做深度技术交流',
    '关注是否有跨 UC 的联动机会',
  ];
}

// Cloudscape StatusIndicator per opp lifecycle + freshness.
function statusIndicator(
  opp: PipelineOpp,
): { type: StatusIndicatorProps.Type; colorOverride?: StatusIndicatorProps.Color } {
  if (opp.oppStatus === OppStatus.LAUNCHED) return { type: 'success' };
  if (opp.oppStatus === OppStatus.CLOSED_LOST) return { type: 'error', colorOverride: 'grey' };
  const days = opp.daysSinceActivity;
  if (days === null || days === undefined) return { type: 'error', colorOverride: 'grey' };
  if (days <= 30) return { type: 'success' };
  return { type: 'stopped', colorOverride: 'red' };
}

// Reorder stages into canonical Figma order + fold non-canonical into Others.
function orderedStages(stages: StageGroup[]): StageGroup[] {
  const present = stages.filter((s) => (s.count ?? 0) > 0);
  const byName = new Map(present.map((s) => [s.stage ?? '', s]));
  const ordered: StageGroup[] = [];
  for (const name of STAGE_ORDER) {
    const g = byName.get(name);
    if (g) {
      ordered.push(g);
      byName.delete(name);
    }
  }
  const remaining = present.filter((s) => byName.has(s.stage ?? ''));
  if (remaining.length > 0) {
    const merged: StageGroup = {
      stage: 'Others',
      count: remaining.reduce((n, s) => n + (s.count ?? 0), 0),
      arr: remaining.reduce((n, s) => n + (s.arr ?? 0), 0),
      opps: remaining.flatMap((s) => s.opps ?? []),
    };
    ordered.push(merged);
  }
  return ordered;
}

// One card per opp — same account with multiple opps produces multiple
// entries side by side, preserving each opp's identity (name + ARR + status).
// Previously opps were merged by accountName which hid every opp beyond the
// first: the header stage summary said "2 Opps, $66K ARR" but the expanded
// list rendered only one card whose ARR was the merged sum — users saw the
// count mismatch and couldn't tell which opps were actually behind it.
function groupByCompany(opps: PipelineOpp[]): { accountName: string; opps: PipelineOpp[] }[] {
  return opps.map((opp) => ({
    accountName: opp.accountName || '(未命名客户)',
    opps: [opp],
  }));
}

// max(relevanceScore) across all activities of all opps under the company.
function companyScore(opps: PipelineOpp[]): number {
  let best = 0;
  for (const opp of opps) {
    for (const a of opp.activities ?? []) {
      const s = a.relevanceScore ?? 0;
      if (s > best) best = s;
    }
  }
  return best;
}

// ── Public component ───────────────────────────────────────────────────────

export default function StageBreakdown({
  detail,
  /** When true, show the UC name inline on every company row — used by the
   *  industry-level detail panel where opps span multiple UCs. */
  showUcName = false,
  /** Client-side pipelineType filter — matches Smithy PipelineType enum
   *  values ('launched_pipelines' etc.). Applied here until the backend
   *  enforces the filter server-side (currently input is echo-only). */
  pipelineTypeFilter,
  /** Extra client-side opp predicate — Account view scopes an industry-level
   *  fetch to a single accountName. Runs alongside pipelineTypeFilter (both
   *  must pass). Untyped Backend input for accountName doesn't exist yet. */
  oppFilter,
}: {
  detail: GetUcPipelineDetailOutput;
  showUcName?: boolean;
  pipelineTypeFilter?: string;
  oppFilter?: (opp: PipelineOpp) => boolean;
}) {
  const stages = useMemo(() => {
    const ordered = orderedStages(detail.stages ?? []);
    if (!pipelineTypeFilter && !oppFilter) return ordered;
    return ordered
      .map((s) => ({
        ...s,
        opps: (s.opps ?? []).filter((o) => {
          if (pipelineTypeFilter && !oppMatchesPipelineType(o, pipelineTypeFilter)) return false;
          if (oppFilter && !oppFilter(o)) return false;
          return true;
        }),
      }))
      .map((s) => ({ ...s, count: s.opps.length }))
      .filter((s) => s.opps.length > 0);
  }, [detail.stages, pipelineTypeFilter, oppFilter]);

  if (stages.length === 0) {
    return (
      <div className="ivt-ucdp__empty">
        {pipelineTypeFilter || oppFilter
          ? `当前过滤条件下无 opp。`
          : '该范围当前池内无 opp。'}
      </div>
    );
  }

  return (
    <div className="ivt-ucdp__stages">
      {stages.map((g) => (
        <StageSection key={g.stage} group={g} showUcName={showUcName} />
      ))}
    </div>
  );
}

// ── Stage section (collapsible) ────────────────────────────────────────────

function StageSection({ group, showUcName }: { group: StageGroup; showUcName: boolean }) {
  const [open, setOpen] = useState(false);
  const stage = group.stage ?? '';
  const label = STAGE_LABELS[stage] || stage;
  const tagColor = STAGE_TAG_COLORS[stage] || STAGE_TAG_COLORS.Others;
  const companies = useMemo(() => groupByCompany(group.opps ?? []), [group.opps]);

  return (
    <div className="ivt-ucdp__stage">
      <button
        type="button"
        className="ivt-ucdp__stage-head"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className="ivt-ucdp__stage-caret">{open ? '▼' : '▶'}</span>
        <span className="ivt-ucdp__stage-swatch" style={{ background: tagColor }} />
        <span className="ivt-ucdp__stage-label">{label}</span>
        <span className="ivt-ucdp__stage-summary">
          {group.count ?? 0} Opps, ${((group.arr ?? 0) / 1e3).toFixed(0)}K ARR
        </span>
      </button>
      {open && (
        <div className="ivt-ucdp__stage-body">
          {companies.map((c) => (
            <CompanyBlock
              key={c.accountName}
              accountName={c.accountName}
              opps={c.opps}
              showUcName={showUcName}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Company block: header row + activity timeline (expanded on click) ──────

function CompanyBlock({
  accountName,
  opps,
  showUcName,
}: {
  accountName: string;
  opps: PipelineOpp[];
  showUcName: boolean;
}) {
  const [open, setOpen] = useState(false);
  const score = companyScore(opps);
  const totalArr = opps.reduce((n, o) => n + (o.totalArr ?? 0), 0);
  const primary = opps[0];
  const scoreStyle =
    score >= 1 && score <= 3 ? RELEVANCE_STYLES[score as 1 | 2 | 3] : undefined;
  const statusInd = statusIndicator(primary);
  const activities: Activity[] = opps.flatMap((o) => o.activities ?? []);

  return (
    <div className="ivt-ucdp__company">
      {/* div + role=button (not <button>) so the SFDC <Link> below is valid
          HTML — nested anchors inside a real <button> are invalid and would
          be flattened by React's a11y checks. */}
      <div
        className="ivt-ucdp__company-head"
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen((v) => !v);
          }
        }}
      >
        <span className="ivt-ucdp__company-caret">{open ? '▼' : '▶'}</span>
        {/* Fixed-width columns so account/score/uc/oppName line up vertically
            across every row (a short account no longer leaves score floating).
            Long text ellipsizes; native title reveals the full string on hover.
            The activity timeline row below uses the SAME column widths so its
            IVT badge falls under this row's score, and its subject falls under
            this row's oppName (Figma 2788-31074). */}
        <span className="ivt-ucdp__company-name" title={accountName}>
          {accountName}
        </span>
        <span
          className="ivt-ucdp__company-score"
          style={scoreStyle ? { color: scoreStyle.color } : undefined}
        >
          {scoreStyle ? `${score}分` : ''}
        </span>
        {/* UC badge + oppName share the same flex column so the UC pill sits
            inline in front of the pipeline name (no fixed column reserved for
            UC — long UC names wouldn't fit and short ones wasted space). */}
        <span className="ivt-ucdp__company-oppname" title={primary.oppName ?? ''}>
          {showUcName && primary.ucName && (
            <span className="ivt-ucdp__company-uc" title={primary.ucName}>
              {primary.ucName}
            </span>
          )}
          {/* oppName IS the SFDC link when we have an oppId — mirrors
              MotionPipelineList. Wrapper span's stopPropagation prevents the
              row-toggle click from firing when the link is clicked. Falls back
              to plain text when oppId is missing. */}
          {primary.oppId ? (
            <span
              className="ivt-ucdp__company-oppname-text"
              onClick={(e) => e.stopPropagation()}
              // Also stop Enter/Space bubbling — otherwise pressing them while
              // the link is focused would both open SFDC AND trigger the
              // row-container's keyDown handler (preventDefault + toggle).
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') e.stopPropagation();
              }}
            >
              <Link
                href={oppSfdcUrl(primary.oppId)}
                external
                target="_blank"
                ariaLabel={`Open ${primary.oppName ?? primary.oppId} in Salesforce`}
              >
                {primary.oppName ?? primary.oppId}
              </Link>
            </span>
          ) : (
            <span className="ivt-ucdp__company-oppname-text">
              {primary.oppName ?? ''}
            </span>
          )}
        </span>
        {primary.statusLabel && (
          <span className="ivt-ucdp__company-status">
            <StatusIndicator type={statusInd.type} colorOverride={statusInd.colorOverride}>
              {stripStatusEmoji(primary.statusLabel)}
            </StatusIndicator>
          </span>
        )}
        <span className="ivt-ucdp__company-arr">${(totalArr / 1e3).toFixed(0)}K</span>
      </div>
      {open && activities.length > 0 && (
        <div className="ivt-ucdp__timeline">
          {activities.map((a, i) => (
            <ActivityTimelineRow key={i} act={a} />
          ))}
        </div>
      )}
      {open && activities.length === 0 && (
        <div className="ivt-ucdp__timeline-empty">该客户暂无活动记录。</div>
      )}
    </div>
  );
}

// ── Activity timeline row (single line + score-tag popover) ────────────────

function ActivityTimelineRow({ act }: { act: Activity }) {
  const score = (act.relevanceScore ?? 0) as 0 | 1 | 2 | 3;
  const scoreStyle = score >= 1 && score <= 3 ? RELEVANCE_STYLES[score as 1 | 2 | 3] : undefined;
  const hasComment = !!(act.comments && act.comments.trim().length > 0);
  const [commentOpen, setCommentOpen] = useState(false);

  const popoverBody = (
    <div className="ivt-ucdp__act-pop">
      {(scoreStyle || act.relevanceAsset) && (
        <div className="ivt-ucdp__act-pop-top">
          {scoreStyle && (
            <div className="ivt-ucdp__act-pop-scorecol">
              <span
                className="ivt-ucdp__act-pop-score"
                style={{ background: scoreStyle.bg, color: scoreStyle.color }}
              >
                {score}分 ({scoreStyle.label})
              </span>
              {act.scoreReason && (
                <span className="ivt-ucdp__act-pop-reason"> — {act.scoreReason}</span>
              )}
            </div>
          )}
          {act.relevanceAsset && (
            <div className="ivt-ucdp__act-pop-assetcol">
              <div className="ivt-ucdp__act-pop-asset-label">关联 Asset</div>
              <a
                className="ivt-ucdp__act-pop-asset-name"
                href={`/ivt/assets?search=${encodeURIComponent(act.relevanceAsset)}`}
              >
                {act.relevanceAsset}
              </a>
            </div>
          )}
        </div>
      )}
      {scoreStyle && (
        <div className="ivt-ucdp__act-pop-actions">
          <div className="ivt-ucdp__act-pop-actions-title">建议下一步</div>
          <ul className="ivt-ucdp__act-pop-actions-list">
            {suggestedActions(score as 1 | 2 | 3, act.relevanceAsset ?? undefined).map((a, i) => (
              <li key={i}>{a}</li>
            ))}
          </ul>
        </div>
      )}
      {!scoreStyle && <div className="ivt-ucdp__act-pop-empty">该活动未评分。</div>}
    </div>
  );

  const subjectText = act.subject || '(无标题活动)';
  // Column-for-column alignment with company row (Figma 2788-31074):
  //   Col 1 (200)  company account   ⟷ activity date
  //   Col 2 (48)   company score     ⟷ activity IVT badge
  //   Col 3 (flex) company [UC pill + oppName] ⟷ activity [type icon + subject]
  //   Col 4 (120)  company status    ⟷ activity score
  //   Col 5 (60)   company arr       ⟷ activity @owner
  // Type icon shares the subject cell just like the UC pill shares oppName.
  return (
    <div className="ivt-ucdp__act-row">
      <span className="ivt-ucdp__act-dot" />
      <span className="ivt-ucdp__act-date">{act.date ?? ''}</span>
      <span className="ivt-ucdp__act-ivt-slot">
        {act.isIvt && <span className="ivt-ucdp__act-ivt">IVT</span>}
      </span>
      {hasComment ? (
        <button
          type="button"
          className="ivt-ucdp__act-subject ivt-ucdp__act-subject--clickable"
          aria-expanded={commentOpen}
          onClick={() => setCommentOpen((v) => !v)}
          title={subjectText}
        >
          <ActivityIcon type={act.type} />
          <span className="ivt-ucdp__act-subject-text">{subjectText}</span>
        </button>
      ) : (
        <span className="ivt-ucdp__act-subject" title={subjectText}>
          <ActivityIcon type={act.type} />
          <span className="ivt-ucdp__act-subject-text">{subjectText}</span>
        </span>
      )}
      <span className="ivt-ucdp__act-score-slot">
        {scoreStyle ? (
          <Popover
            position="right"
            size="large"
            triggerType="custom"
            dismissButton
            renderWithPortal
            header={act.subject || '活动详情'}
            content={popoverBody}
          >
            <button
              type="button"
              className="ivt-ucdp__act-score-tag"
              style={{ color: scoreStyle.color }}
              aria-label={`${score}分,查看评分详情`}
            >
              {score}分
            </button>
          </Popover>
        ) : null}
      </span>
      <span className="ivt-ucdp__act-owner">
        {act.alias ? `@${act.alias}` : ''}
      </span>
      {hasComment && commentOpen && (
        <div className="ivt-ucdp__act-comments" title={act.comments ?? undefined}>
          {act.comments}
        </div>
      )}
    </div>
  );
}
